import React, { useMemo } from 'react'
import _cx from 'classnames/bind'
import Markdown from 'markdown-to-jsx'
import DOMPurify from 'dompurify'
import Text from '../../elementals/text'
import type { AutomationTarget } from '../../../types'
import cls from './styles.scss'

// eslint-disable-next-line @typescript-eslint/no-unsafe-argument
const cx = _cx.bind(cls)

interface MarkdownRendererProps extends AutomationTarget {
  content: string
  className?: string
}

export type { MarkdownRendererProps }

/**
 * Renders markdown content with proper sanitization and styling
 * @param props - Component props
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  className,
  elmId = 'markdown-renderer',
}) => {
  // Sanitize content to prevent XSS attacks
  const sanitizedContent = useMemo(() => {
    return DOMPurify.sanitize(content, {
      ALLOWED_TAGS: [
        'p',
        'br',
        'strong',
        'b',
        'em',
        'i',
        'u',
        'a',
        'code',
        'pre',
        'blockquote',
        'h1',
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'ul',
        'ol',
        'li',
        'table',
        'thead',
        'tbody',
        'tr',
        'th',
        'td',
        'hr',
        'img',
        'del',
        's',
        'mark',
        'sup',
        'sub',
      ],
      ALLOWED_ATTR: [
        'href',
        'target',
        'rel',
        'title',
        'alt',
        'width',
        'height',
        'src',
        'class',
      ],
      ALLOW_DATA_ATTR: false,
    })
  }, [content])

  // Configure markdown-to-jsx options
  const markdownOptions = useMemo(
    () => ({
      overrides: {
        // Map headers to Text components
        h1: {
          component: Text.Header,
          props: { level: 1 },
        },
        h2: {
          component: Text.Header,
          props: { level: 2 },
        },
        h3: {
          component: Text.Header,
          props: { level: 3 },
        },
        h4: {
          component: Text.Header,
          props: { level: 4 },
        },
        h5: {
          component: Text.Header,
          props: { level: 5 },
        },
        h6: {
          component: Text.Header,
          props: { level: 6 },
        },
        // Style links
        a: {
          props: {
            target: '_blank',
            rel: 'noopener noreferrer',
          },
        },
        // Style code elements
        code: {
          props: {
            className: cx('inline-code'),
          },
        },
        pre: {
          props: {
            className: cx('code-block'),
          },
        },
        // Style blockquotes
        blockquote: {
          props: {
            className: cx('blockquote'),
          },
        },
        // Style lists
        ul: {
          props: {
            className: cx('list', 'unordered'),
          },
        },
        ol: {
          props: {
            className: cx('list', 'ordered'),
          },
        },
        // Style tables
        table: {
          props: {
            className: cx('table'),
          },
        },
        // Style horizontal rule
        hr: {
          props: {
            className: cx('divider'),
          },
        },
      },
      // Disable HTML parsing to ensure only markdown is rendered
      disableParsingRawHTML: false,
    }),
    []
  )

  return (
    <div className={cx('markdown-renderer', className)} data-elm-id={elmId}>
      <Markdown options={markdownOptions}>{sanitizedContent}</Markdown>
    </div>
  )
}

export default React.memo(MarkdownRenderer)
